import React, { useEffect, useRef, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Download, Users, UserCheck, UserPlus, TrendingUp, Activity, RefreshCw, Clock, Calendar } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { getTenantMonthlyReport, getTenantMonthlyReportForUser } from '../store/statisticsThunks';
import { useDispatch, useSelector } from 'react-redux';
import './monthlyReport.css';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
export default function MonthlyReport() {
  const { tenantId } = useParams();
  const urlParams = new URLSearchParams(window.location.search);
  const s = urlParams.get('s');
  const ts = urlParams.get('t');
  const { reportData } = useSelector((state) => state.statistics);
  const dispatch = useDispatch();
  const [isPrinting, setIsPrinting] = useState(false); // État pour masquer le bouton
  const { t } = useTranslation();
  // Création de refs pour les deux pages
  const page1Ref = useRef(null);
  const page2Ref = useRef(null);
  const page3Ref = useRef(null);
  useEffect(() => {
    if (tenantId && s && ts) {
      dispatch(getTenantMonthlyReport({
        params: [{ tenantId: tenantId }],
        options: {
          query: {
            s: s,
            t: ts
          }
        }
      }));
    }
    else {
      dispatch(getTenantMonthlyReportForUser());
    }
  }, [tenantId, s, ts]);




  const handleExportPDF = async () => {
    setIsPrinting(true);
    const pdf = new jsPDF('p', 'mm', 'a4');

    const margin = 10;
    const pdfWidth = pdf.internal.pageSize.getWidth() - (margin * 2);
    const pdfHeightLimit = pdf.internal.pageSize.getHeight() - (margin * 2);

    const refs = [page1Ref, page2Ref, page3Ref];

    try {
      for (let i = 0; i < refs.length; i++) {
        const ref = refs[i];
        if (ref?.current) {
          const canvas = await html2canvas(ref.current, {
            scale: 2,
            useCORS: true,
            logging: false,
            windowWidth: 1000, // Aligné avec le CSS ci-dessus
            backgroundColor: '#ffffff'
          });

          const imgData = canvas.toDataURL('image/png');
          const imgProps = pdf.getImageProperties(imgData);

          const imgDisplayHeight = (imgProps.height * pdfWidth) / imgProps.width;

          if (i > 0) pdf.addPage();

          pdf.addImage(
            imgData,
            'PNG',
            margin,
            margin,
            pdfWidth,
            imgDisplayHeight > pdfHeightLimit ? pdfHeightLimit : imgDisplayHeight
          );
        }
      }
      pdf.save(`Rapport_${reportData.tenant.name}.pdf`);
    } catch (err) {
      console.error("Erreur export:", err);
    } finally {
      setIsPrinting(false);
    }
  };
  const formatPeakInterval = (peakInterval) => {
    if (!peakInterval) return "";

    // 1. On sépare la date de l'heure
    const [datePart, timePart] = peakInterval.split(' | ');

    // 2. On transforme la date en objet Date
    const dateObj = new Date(datePart);

    // 3. On formate selon la locale française
    const formattedDate = dateObj.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short', // 'short' donne "janv."
      year: 'numeric'
    });
    if (timePart) {
      return `${formattedDate} de ${timePart}`;

    }
    return `${formattedDate}`;

  };
  const formatPeakIntervalwithoutTime = (peakInterval) => {
    if (!peakInterval) return "";

    // 1. On sépare la date de l'heure
    const [datePart, timePart] = peakInterval.split(' | ');

    // 2. On transforme la date en objet Date
    const dateObj = new Date(datePart);

    // 3. On formate selon la locale française
    const formattedDate = dateObj.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short', // 'short' donne "janv."
      year: 'numeric'
    });

    return `${formattedDate}`;

  };
  const formatReportPeriod = (month, year) => {
    if (!month || !year) return "";

    // On crée une date fictive (le 1er du mois en question)
    // month - 1 car les mois commencent à 0 en JavaScript (0 = Janvier)
    const dateObj = new Date(year, parseInt(month) - 1, 1);

    const monthName = dateObj.toLocaleDateString('fr-FR', { month: 'short' });

    // On retire le point et on met éventuellement la première lettre en majuscule
    const cleanMonth = monthName.replace('.', '');
    const capitalizedMonth = cleanMonth.charAt(0).toUpperCase() + cleanMonth.slice(1);

    return `${capitalizedMonth} ${year}`;
  };
  if (!reportData || !reportData.data) return null;

  const chartData = reportData.data.activité.evolutionQuotidienne.map((item) => ({
    date: new Date(item.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
    actifs: parseInt(item.count),
  }));

  // Composant interne pour les cartes avec style standard
  const StatCard = ({ title, value, subtext, trend, icon: Icon }) => (
    <div className="card h-100 shadow-sm border-0 card-standard" style={{ borderLeft: '0.25rem solid #60D744' }}>
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <span className="text-uppercase text-muted" style={{ fontSize: '0.9rem', fontWeight: 500 }}>{title}</span>
          {Icon && <Icon size={18} className="text-muted" style={{ opacity: 0.7 }} />}
        </div>
        <h3 className="fw-normal mb-1" style={{ color: '#1a202c' }}>{value}</h3>
        {trend ? (
          <div className={`small ${parseFloat(trend) >= 0 ? 'text-success' : 'text-danger'}`} style={{ fontWeight: 400 }}>
            {parseFloat(trend) >= 0 ? '↑' : '↓'} {trend}%
          </div>
        ) : (
          <div className="text-standard-muted" style={{ fontSize: '0.9rem' }}>{subtext}</div>
        )}
      </div>
    </div>
  );
  const perf = reportData.data?.performance;

  return (
    <div className="report-page-container bg-white">

      <div className="container d-flex justify-content-end py-3 no-print">
        <button
          className="btn d-flex align-items-center gap-2 shadow-sm"
          style={{
            backgroundColor: '#60D744',
            borderColor: '#60D744',
            color: '#white',
            fontWeight: '600',
            padding: '0.625rem 1.25rem'
          }}
          onClick={handleExportPDF}
          disabled={isPrinting} // Désactivé si export local ou chargement backend
        >
          {isPrinting ? (
            <>
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              {t("exporting...")}
            </>
          ) : (
            <>
              <Download size={18} />
              {t("export_pdf")}
            </>
          )}
        </button>
      </div>



      <div className="container py-4">


        {/* PAGE 1: Couverture */}
        <div
          ref={page1Ref}
          className="first-page-cover bg-white"
        >
          {/* Ce wrapper permet de remonter tout le bloc si nécessaire */}
          <div className="content-wrapper text-center w-100">

            {/* Logo central */}
            <img
              src='/Aifel.svg'
              alt="logo"
              style={{ width: '7.5rem', height: '7.5rem', marginBottom: '1.875rem' }}
            />

            {/* Titre Principal */}
            <h1 className="fw-bold" style={{ fontSize: '2.1875rem', color: '#1a202c', marginBottom: '0.625rem' }}>
              {t("Monthly-Report")}
            </h1>

            {/* Ligne verte de séparation (comme sur l'image) */}
            <div
              className="mx-auto"
              style={{ borderTop: '0.25rem solid #60D744', width: '5rem', marginBottom: '3.75rem' }}
            ></div>

            {/* Bloc de détails aligné à gauche (mais centré par rapport au parent) */}
            <div className="d-inline-block text-start" style={{ minWidth: '18.75rem' }}>
              <p className="mb-2" style={{ fontSize: '1.1rem' }}>
                <strong>{t("tenant")} :</strong> {reportData.tenant.name}
              </p>
              <p className="mb-2" style={{ fontSize: '1.1rem' }}>
                <strong>{t("PERIOD")} :</strong> {formatReportPeriod(reportData.period.month, reportData.period.year)}
              </p>
              <p className="mb-4" style={{ fontSize: '1.1rem' }}>
                <strong>{t("Generated")} :</strong> {formatPeakInterval(reportData.date)}
              </p>

              {/* Description subtile en dessous */}
              <p className="text-muted" style={{ fontSize: '0.95rem', maxWidth: '28.125', lineHeight: '1.5' }}>
                {t("rapport_synthese_utilisation", { tenant: reportData.tenant.name })}
              </p>
            </div>
          </div>
        </div>

        <div ref={page2Ref} >
          <div className="mb-4">
            <h4 className="section-title-standard border-start border-4 border-success ps-3 mb-4">
              {t("synthese_executive")}
            </h4>

            <div className="alert alert-light border-0 shadow-sm mb-4 p-3">
              <p className="mb-0">
                {t("synthese_description_prefix")}

                {/* Section Collaborateurs */}
                <strong> {reportData.data.synthèse.utilisateursActifs} {t("collaborateurs")}</strong>
                ({reportData.data.synthèse.tendanceUsers >= 0 ? t("soit_une_hausse_de") : t("soit_une_baisse_de")}
                <span className={`fw-bold mx-1 ${reportData.data.synthèse.tendanceUsers >= 0 ? "text-success" : "text-danger"}`}>
                  {Math.abs(reportData.data.synthèse.tendanceUsers)}%
                  {reportData.data.synthèse.tendanceUsers >= 0 ? " ↑" : " ↓"}
                </span>)

                {t("et")}

                {/* Section Invités - CORRIGÉE */}
                <strong> {reportData.data.synthèse.invitesActifs} {t("invites")}</strong>
                ({reportData.data.synthèse.tendanceGuests >= 0 ? t("soit_une_hausse_de") : t("soit_une_baisse_de")}
                <span className={`fw-bold mx-1 ${reportData.data.synthèse.tendanceGuests >= 0 ? "text-success" : "text-danger"}`}>
                  {Math.abs(reportData.data.synthèse.tendanceGuests)}%
                  {reportData.data.synthèse.tendanceGuests >= 0 ? " ↑" : " ↓"}
                </span>)

                {t("par_rapport_mois_dernier")}.
                {t("synthese_description_suffix")}
              </p>
            </div>

            <div className="row g-4 mb-5">
              <div className="col-md-3"><StatCard title={t("utilisateurs_totaux")} value={reportData.data.synthèse.utilisateursTotaux} icon={Users} /></div>
              <div className="col-md-3"><StatCard title={t("utilisateurs_actifs")} value={reportData.data.synthèse.utilisateursActifs} trend={reportData.data.synthèse.tendanceUsers} icon={UserCheck} /></div>
              <div className="col-md-3"><StatCard title={t("invites_totaux")} value={reportData.data.synthèse.invitesTotaux} icon={UserPlus} /></div>
              <div className="col-md-3"><StatCard title={t("invites_actifs")} value={reportData.data.synthèse.invitesActifs} trend={reportData.data.synthèse.tendanceGuests} icon={UserCheck} /></div>
            </div>
          </div>

          <div className="mb-4">
            <h4 className="section-title-standard border-start border-4 border-success ps-3 mb-4">{t("activite_et_engagement")}</h4>

            <div className="alert alert-light border-0 shadow-sm mb-4 p-3">
              <p className="mb-0">
                {t("activite_description_prefix")}
                <strong> {reportData.data.activité.nouveauxUsers} {t("nouveaux_membres")} </strong>
                {t("ont_rejoint_la_plateforme")}.
                {t("le_taux_engagement_est_de")}
                <span className="fw-bold mx-1 text-success">
                  {reportData.data.activité.tauxActivite}%
                </span>
                {t("avec_un_total_de")}
                <strong> {reportData.data.activité.utilisateursRecurrents} </strong>
                {t("utilisateurs_recurrents_fidele")}.
              </p>
            </div>





            <div className="row g-4 mb-5">
              <div className="col-12 col-md-3">
                <StatCard
                  title={t("nouveaux_inscrits")}
                  value={reportData.data.activité.nouveauxUsers}
                  subtext={t("comptes_crees_ce_mois")}
                  icon={UserPlus}
                />
              </div>
              <div className="col-12 col-md-3">
                <StatCard
                  title={t("nouveaux_actifs")}
                  value={reportData.data.activité.nouveauxActiveUsers}
                  subtext={t("inscrits_et_connectes")}
                  icon={Activity}
                />            </div>
              <div className="col-12 col-md-3">
                <StatCard
                  title={t("utilisateurs_recurrents")}
                  value={reportData.data.activité.utilisateursRecurrents}
                  subtext={t("fidelite_sur_la_periode")}
                  icon={RefreshCw}
                />            </div>
              <div className="col-12 col-md-3">
                <StatCard
                  title={t("taux_d_activite")}
                  value={`${reportData.data.activité.tauxActivite}%`}
                  subtext={t("engagement_global")}
                  icon={Activity}
                />            </div>
            </div>
          </div>
          <div className="alert alert-light border-0 shadow-sm mb-4 p-3">
            <p className="mb-0">
              <i className="bi bi-graph-up-arrow me-2"></i>
              {t("graph_description_daily_users")}
            </p>
          </div>
          {/* Graphique */}
          <div className="card card-standard shadow-sm mb-5">
            <div className="card-body p-4">
              {/* Titre avec votre style spécifique Poppins 600 */}
              <h4 className="section-title-standard mb-4">{t("evolution_quotidienne")}</h4>

              <div style={{ width: '100%', height: 350 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />

                    {/* Axe X : Écriture standard et fine */}
                    <XAxis
                      dataKey="date"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#888', fontWeight: 400 }}
                    />

                    {/* Axe Y : Forcé sur des nombres entiers (pas de 1.5, 2.5...) */}
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#888', fontWeight: 400 }}
                      allowDecimals={false}  /* Désactive les nombres à virgule */
                      domain={[0, 'auto']}    /* Commence toujours à 0 */
                      dx={-5}                 /* Ajustement léger de la position */
                    />

                    <Tooltip
                      cursor={{ fill: '#f8fff8' }}
                      contentStyle={{
                        border: 'none',
                        borderRadius: '0.5rem',
                        boxShadow: '0 0.125rem 0.625rem rgba(0,0,0,0.1)',
                        fontFamily: 'sans-serif',
                        fontSize: '0.8125rem'
                      }}
                    />

                    <Bar
                      dataKey="actifs"
                      fill="#60D744"
                      radius={[4, 4, 0, 0]}
                      barSize={30}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <p className="text-center text-muted mt-3" style={{ fontSize: '0.9rem', fontWeight: 300 }}>
                {t("nombre_total_utilisateurs_par_jour")}
              </p>
            </div>
          </div>
        </div>

        {/* PERFORMANCE SECTION */}
        {perf && (
          <div ref={page3Ref} >
            <div className="mb-4">
              <h4 className="section-title-standard border-start border-4 border-success ps-3 mb-4">{t("performance_systeme")}</h4>

              <div className="alert alert-light border-0 shadow-sm mb-4 p-3">
                <p className="mb-0">
                  {t("performance_description_start")}
                  <strong> {perf.max_connection} {t("connections")} </strong>
                  {t("recorded_on")}
                  <strong> {formatPeakInterval(perf.most_active_day)}</strong>.

                  {t("peak_time_observation")}
                  <span className="text-success fw-bold"> {formatPeakInterval(perf.peak_interval)} </span>,
                  {t("performance_description_peak", { count: perf.peak_users })}

                </p>
              </div>
              <div className="row g-4 mb-3">
                <div className="col-md-3">
                  <StatCard
                    title={t("jour_plus_actif")}
                    value={formatPeakIntervalwithoutTime(perf.most_active_day)} // Utilisation de la fonction

                    subtext={t("frequentation_maximale")}
                    icon={Calendar}
                  />
                </div>
                <div className="col-md-3">
                  <StatCard
                    title={t("connexions_max")}
                    value={perf.max_connection}
                    subtext={t("en_une_journee")}
                    icon={TrendingUp}
                  />
                </div>
                <div className="col-md-3">
                  <StatCard
                    title={t("pic_activite")}
                    value={perf.peak_users}
                    subtext={t("utilisateurs_simultanes")}
                    icon={Activity}
                  />
                </div>
                <div className="col-md-3">
                  <StatCard
                    title={t("intervalle_de_pointe")}
                    value={formatPeakInterval(perf.peak_interval)} // Utilisation de la fonction
                    subtext={t("heure_affluence")}
                    icon={Clock}
                  />
                </div>
              </div>
              <div className="alert alert-light border-0 shadow-sm mb-4 p-3">
                <p className="mb-0">
                  {t("graph_description_text")}{" "} :
                  <span className="fw-bold">{formatPeakIntervalwithoutTime(perf.peak_interval)}</span>.

                </p>
              </div>
              {perf?.hourly_breakdown && (
                <div className="card card-standard shadow-sm mt-4 border-0">
                  <div className="card-body p-4">
                    <h5 className="section-title-standard mb-4" style={{ fontSize: '1.125rem', fontWeight: 600 }}>
                      {t("jour_plus_actif")} : {formatPeakIntervalwithoutTime(perf.most_active_day)}
                    </h5>

                    <div style={{ width: '100%', height: 300 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={perf.hourly_breakdown}
                          margin={{ top: 20, right: 10, left: -25, bottom: 10 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#ececec" />
                          <XAxis
                            dataKey="hour"
                            interval={0}
                            tickFormatter={(value) => value.replace(/h/g, '')}
                            tick={{ fontSize: 9, fill: '#A0A0A0' }}
                            angle={-45} // Incline le texte pour gagner de la place
                            textAnchor="end"
                            height={50} // Augmente l'espace en bas pour le texte incliné
                          />
                          <YAxis
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 13, fill: '#A0A0A0' }}
                            allowDecimals={false}
                          />
                          <Tooltip
                            cursor={{ fill: 'rgba(96, 215, 68, 0.1)' }}
                            contentStyle={{ border: 'none', borderRadius: '0.5rem', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                            labelFormatter={(value) => `Tranche horaire : ${value}`}
                          />
                          <Bar
                            dataKey="users"
                            fill="#60D744"
                            radius={[4, 4, 0, 0]}
                            barSize={15} // Barre un peu plus large pour mieux voir le centrage
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}